import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { FaBars, FaTimes, FaHome, FaInfoCircle, FaLayerGroup, FaCode, FaWallet, FaEnvelope } from 'react-icons/fa';
import logo from '../assets/logo.png';

const HeaderContainer = styled.header`
  background-color: ${props => props.theme.colors.white};
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  position: sticky;
  top: 0;
  z-index: 100;
`;

const Nav = styled.nav`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 2rem;
  max-width: 1200px;
  margin: 0 auto;
`;

const Logo = styled.div`
  img {
    height: 40px;
  }
`;

const MenuIcon = styled.div`
  display: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: ${props => props.theme.colors.secondary};
  transition: ${props => props.theme.transition};
  
  &:hover {
    color: ${props => props.theme.colors.primary};
    transform: scale(1.1);
  }
  
  @media (max-width: ${props => props.theme.breakpoints.md}) {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 40px;
    height: 40px;
    border-radius: 8px;
    background: linear-gradient(135deg, #ffffff 0%, #f8fcff 100%);
    box-shadow: 0 4px 10px rgba(0, 0, 0, 0.05);
    border: 1px solid rgba(0, 213, 146, 0.1);
  }
`;

const MobileMenuOverlay = styled.div`
  display: none;
  
  @media (max-width: ${props => props.theme.breakpoints.md}) {
    display: ${({ isOpen }) => (isOpen ? 'block' : 'none')};
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(0, 0, 0, 0.5);
    backdrop-filter: blur(3px);
    z-index: 99;
    transition: opacity 0.3s ease;
    opacity: ${({ isOpen }) => (isOpen ? '1' : '0')};
  }
`;

const NavLinks = styled.ul`
  display: flex;
  list-style: none;
  
  @media (max-width: ${props => props.theme.breakpoints.md}) {
    display: ${({ isOpen }) => (isOpen ? 'flex' : 'none')};
    position: fixed;
    top: 0;
    right: ${({ isOpen }) => (isOpen ? '0' : '-260px')};
    width: 250px;
    height: 100vh;
    background: linear-gradient(135deg, #1a1f25 0%, #242a35 100%);
    flex-direction: column;
    align-items: flex-start;
    justify-content: flex-start;
    transition: all 0.4s cubic-bezier(0.165, 0.84, 0.44, 1);
    box-shadow: -5px 0 25px rgba(0, 0, 0, 0.25);
    z-index: 100;
    padding: 4.5rem 0 2rem 0;
    overflow-y: auto;
    
    &::before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      width: 4px;
      height: 80px;
      background: linear-gradient(to bottom, ${props => props.theme.colors.primary}, transparent);
      opacity: 0.8;
    }
  }
`;

const NavItem = styled.li`
  margin: 0 1rem;
  position: relative;
  
  @media (max-width: ${props => props.theme.breakpoints.md}) {
    margin: 0.2rem 0;
    width: 100%;
  }
`;

const NavLink = styled(Link)`
  color: ${props => props.theme.colors.secondary};
  font-weight: 500;
  transition: ${props => props.theme.transition};
  position: relative;
  display: flex;
  align-items: center;
  
  svg {
    display: none;
  }
  
  &:after {
    content: '';
    position: absolute;
    width: 0;
    height: 2px;
    bottom: -5px;
    left: 0;
    background-color: ${props => props.theme.colors.primary};
    transition: ${props => props.theme.transition};
  }
  
  &:hover, &.active {
    color: ${props => props.theme.colors.primary};
    
    &:after {
      width: 100%;
    }
  }
  
  @media (max-width: ${props => props.theme.breakpoints.md}) {
    padding: 0.75rem 1.5rem;
    width: 100%;
    font-size: 1rem;
    letter-spacing: 0.3px;
    color: rgba(255, 255, 255, 0.85);
    
    svg {
      display: inline-block;
      margin-right: 0.8rem;
      font-size: 1.1rem;
      color: rgba(255, 255, 255, 0.6);
    }
    
    &:after {
      display: none;
    }
    
    &:hover {
      color: #ffffff;
      background: rgba(0, 213, 146, 0.15);
      
      svg {
        color: ${props => props.theme.colors.primary};
      }
    }
    
    &.active {
      color: #ffffff;
      background: rgba(0, 213, 146, 0.2);
      border-left: 3px solid ${props => props.theme.colors.primary};
      padding-left: calc(1.5rem - 3px);
      
      svg {
        color: ${props => props.theme.colors.primary};
      }
    }
  }
`;

const ConnectButton = styled.button`
  background-color: ${props => props.theme.colors.primary};
  color: ${props => props.theme.colors.white};
  border: none;
  padding: 0.7rem 1.5rem;
  border-radius: ${props => props.theme.borderRadius};
  font-weight: 500;
  transition: ${props => props.theme.transition};
  
  &:hover {
    background-color: ${props => props.theme.colors.secondary};
    transform: translateY(-2px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
  }
  
  @media (max-width: ${props => props.theme.breakpoints.md}) {
    margin: 1rem 1.5rem 0;
    width: calc(100% - 3rem);
    padding: 0.7rem 1rem;
    background: ${props => props.theme.colors.primary};
    border-radius: 6px;
    font-size: 0.9rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    position: relative;
    overflow: hidden;
    box-shadow: 0 4px 15px rgba(0, 0, 0, 0.3);
    
    &:hover {
      background-color: ${props => props.theme.colors.primary};
      transform: translateY(0);
      filter: brightness(1.1);
    }
  }
`;

const MobileMenuHeader = styled.div`
  display: none;
  
  @media (max-width: ${props => props.theme.breakpoints.md}) {
    display: flex;
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 60px;
    align-items: center;
    justify-content: space-between;
    padding: 0 1.5rem;
    border-bottom: 1px solid rgba(0, 213, 146, 0.15);
    background-color: rgba(26, 31, 37, 0.9);
    backdrop-filter: blur(5px);
    
    h3 {
      font-size: 1.2rem;
      color: #ffffff;
      font-weight: 600;
      margin: 0;
      
      span {
        color: ${props => props.theme.colors.primary};
      }
    }
  }
`;

const CloseButton = styled.div`
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: rgba(0, 213, 146, 0.2);
  color: ${props => props.theme.colors.primary};
  font-size: 1.1rem;
  cursor: pointer;
  transition: all 0.3s ease;
  display: none;
  
  &:hover {
    background: ${props => props.theme.colors.primary};
    color: #ffffff;
    transform: rotate(90deg);
  }
  
  @media (max-width: ${props => props.theme.breakpoints.md}) {
    display: flex;
  }
`;


const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isConnected, setIsConnected] = useState(false);
  const [walletAddress, setWalletAddress] = useState('');
  const location = useLocation();
  
  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };
  
  const connectWallet = async () => {
    if (window.ethereum) {
      try {
        const accounts = await window.ethereum.request({ method: 'eth_requestAccounts' });
        setWalletAddress(accounts[0]);
        setIsConnected(true);
        
        // Request to add RDT Chain to MetaMask
        await addRDTChainToMetaMask();
      } catch (error) {
        console.error('Error connecting wallet:', error);
      }
    } else {
      alert('Please install MetaMask or another EVM compatible wallet to connect!');
    }
  };
  
  const addRDTChainToMetaMask = async () => {
    try {
      await window.ethereum.request({
        method: 'wallet_addEthereumChain',
        params: [
          {
            chainId: '0xD5CFF', // 875423 in hex
            chainName: 'RDT Chain',
            nativeCurrency: {
              name: 'RDT',
              symbol: 'RDT',
              decimals: 18,
            },
            rpcUrls: ['https://rpc-supernova.rdtchain.com'],
            blockExplorerUrls: ['https://explorer.rdtchain.com'],
          },
        ],
      });
    } catch (error) {
      console.error('Error adding RDT Chain to MetaMask:', error);
    }
  };
  
  // Close menu when clicking outside on mobile
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isMenuOpen && !event.target.closest('nav')) {
        setIsMenuOpen(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isMenuOpen]);
  
  // Close menu when route changes
  useEffect(() => {
    setIsMenuOpen(false);
  }, [location]);
  
  return (
    <HeaderContainer>
      <Nav>
        <Logo>
          <Link to="/">
            <img src={logo} alt="RDT Chain Logo" />
          </Link>
        </Logo>
        
        <MenuIcon onClick={toggleMenu}>
          <FaBars />
        </MenuIcon>
        
        <MobileMenuOverlay isOpen={isMenuOpen} onClick={toggleMenu} />
        
        <NavLinks isOpen={isMenuOpen}>
          <MobileMenuHeader>
            <h3>RDT <span>Chain</span></h3>
            <CloseButton onClick={toggleMenu}>
              <FaTimes />
            </CloseButton>
          </MobileMenuHeader>
          
          <NavItem>
            <NavLink to="/" className={location.pathname === '/' ? 'active' : ''}>
              <FaHome />
              Home
            </NavLink>
          </NavItem>
          
          <NavItem>
            <NavLink to="/about" className={location.pathname === '/about' ? 'active' : ''}>
              <FaInfoCircle />
              About
            </NavLink>
          </NavItem>
          
          <NavItem>
            <NavLink to="/ecosystem" className={location.pathname === '/ecosystem' ? 'active' : ''}>
              <FaLayerGroup />
              Ecosystem
            </NavLink>
          </NavItem>
          
          <NavItem>
            <NavLink to="/developers" className={location.pathname === '/developers' ? 'active' : ''}>
              <FaCode />
              Developers
            </NavLink>
          </NavItem>
          
          <NavItem>
            <NavLink to="/wallet" className={location.pathname === '/wallet' ? 'active' : ''}>
              <FaWallet />
              Wallet
            </NavLink>
          </NavItem>
          
          <NavItem>
            <NavLink to="/contact" className={location.pathname === '/contact' ? 'active' : ''}>
              <FaEnvelope />
              Contact
            </NavLink>
          </NavItem>
          
          {isConnected ? (
            <NavItem>
              <NavLink to="#">
                {`${walletAddress.substring(0, 6)}...${walletAddress.substring(walletAddress.length - 4)}`}
              </NavLink>
            </NavItem>
          ) : (
            <NavItem>
              <ConnectButton onClick={connectWallet}>Connect Wallet</ConnectButton>
            </NavItem>
          )}
          
        </NavLinks>
      </Nav>
    </HeaderContainer>
  );
};

export default Header;